import { useEffect, useMemo, useRef, useState } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { logConnectorFailure } from "../../../telemetry";
import { fetchMyAddresses, type MyAddresses } from "../services/myAddresses";
import type { Self } from "../types";

/**
 * Who "you" are in the calendar: your name, the address of the account you
 * signed in to AiVA with, and every address of that mailbox. Starts with
 * Outlook's profile address, then takes the rest from Graph, so an invite sent
 * to an alias still shows you as "You" and not as an attendee. If Graph can't
 * be read, the profile address is used alone.
 */
export function useSelf(token: string | null): Self {
  const { refreshGraphToken } = useAuthState();
  const [mine, setMine] = useState<MyAddresses>({ primary: "", all: [] });
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const hasToken = Boolean(token);

  const profile = useMemo(() => {
    const p = Office.context?.mailbox?.userProfile;
    return { name: p?.displayName ?? "", email: (p?.emailAddress ?? "").trim().toLowerCase() };
  }, []);

  useEffect(() => {
    const current = tokenRef.current;
    if (!current) return;
    let cancelled = false;
    fetchMyAddresses(current, () => refreshGraphToken(false))
      .then((addresses) => {
        if (!cancelled) setMine(addresses);
      })
      .catch((e: unknown) => {
        if (!cancelled) logConnectorFailure("calendar", e, "load-my-addresses");
      });
    return () => {
      cancelled = true;
    };
  }, [hasToken, refreshGraphToken]);

  return useMemo(
    () => ({
      name: profile.name,
      email: mine.primary || profile.email,
      emails: new Set([profile.email, ...mine.all].filter(Boolean)),
    }),
    [profile, mine],
  );
}
